import { DragEvent, useEffect, useMemo, useRef, useState } from "react";

import { ArrowLeftIcon, ArrowRightIcon, ChevronDownIcon, PhotoIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";

import Modal from "../../layouts/Modal";
import usePartDetails from "../../data/usePartDetails";
import useSinglePartMutations from "../../data/useSinglePartMutations";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { IPart, IPartImage, IPartImagePatchRequest } from "../../models/Part";
import { IListingOptimisation } from "../../models/listingOptimisation/IListingOptimisation";
import classNames from "../../utils/classNames";
import EditListingImages from "../ebayListings/EditListingImages";
import { MIN_IMAGE_COUNT } from "../ebayListings/listedTable/listingScore";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import LoadingWheel from "../shared/LoadingWheel";
import { mergeRowInsights } from "./queueRowSignals";

export type StagedImageFlag = "removeBackgroundFirstImage" | "applyImageRules";

interface IPhotoManagerModalProps {
  part: IPart | null;
  open: boolean;
  onClose: () => void;
  optimisation: IListingOptimisation | undefined;
  /** Stages a relist-time image flag (background removal on the 1st, image rules) - not applied now. */
  onStageFlag: (part: IPart, flag: StagedImageFlag, value: boolean) => void;
  staging: boolean;
}

const isPhoto = (image: IPartImage) => !image.deleted && !image.url?.includes("mp4") && !image.isEndSlate;

/** Photos in the order eBay would get them: ordered ones first, then the rest as uploaded. */
export function orderedPhotos(images: IPartImage[] | undefined): IPartImage[] {
  return (images ?? [])
    .map((image, index) => ({ image, index }))
    .filter(({ image }) => isPhoto(image))
    .sort((a, b) => {
      const orderA = a.image.order ?? Number.MAX_SAFE_INTEGER;
      const orderB = b.image.order ?? Number.MAX_SAFE_INTEGER;
      return orderA - orderB || a.index - b.index;
    })
    .map(({ image }) => image);
}

/**
 * The queue's photo manager. The tiles, the drag / arrow reorder, delete and Add photo are the
 * design's; they write to the part straight away, like the photo manager everywhere else. The
 * existing editor (crop, background removal, auto order, image settings) sits underneath.
 */
export default function PhotoManagerModal({ part, open, onClose, optimisation, onStageFlag, staging }: IPhotoManagerModalProps) {
  const partId = part?.id;
  const { partDetails, isLoading } = usePartDetails(partId, open && !!partId);
  const fullPart = useMemo(() => (part ? mergeRowInsights(part, partDetails) : null), [part, partDetails]);

  const { uploadPicture, patchPicture, removeImage } = useSinglePartMutations(partId);
  const { saveHandlers } = useDefaultCRUDHandlers("Photo order");
  const { deleteHandlers } = useDefaultCRUDHandlers("Photo");

  const serverPhotos = useMemo(() => orderedPhotos(fullPart?.images), [fullPart?.images]);
  // Shown order while a reorder is in flight, so the tiles move before the server answers.
  const [draft, setDraft] = useState<IPartImage[] | null>(null);
  const photos = draft ?? serverPhotos;

  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) {
      setDraft(null);
      setDragIndex(null);
      setOverIndex(null);
      setEditorOpen(false);
    }
  }, [open]);

  useEffect(() => {
    setDraft(null);
  }, [serverPhotos]);

  const saveOrder = (next: IPartImage[]) => {
    setDraft(next);
    const body: IPartImagePatchRequest[] = next.map((image, index) => ({
      imageId: image.id,
      order: index,
      isThumbnail: index === 0,
      useOnEbay: true,
    }));
    patchPicture.mutate(body, {
      onError: () => {
        setDraft(null);
        saveHandlers.onError();
      },
    });
  };

  const move = (from: number, to: number) => {
    if (from === to || to < 0 || to >= photos.length) return;
    const next = [...photos];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    saveOrder(next);
  };

  const handleDragStart = (index: number) => (e: DragEvent<HTMLLIElement>) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(index));
  };

  const handleDragOver = (index: number) => (e: DragEvent<HTMLLIElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (overIndex !== index) setOverIndex(index);
  };

  const handleDrop = (index: number) => (e: DragEvent<HTMLLIElement>) => {
    e.preventDefault();
    const from = dragIndex ?? Number(e.dataTransfer.getData("text/plain"));
    setDragIndex(null);
    setOverIndex(null);
    if (!Number.isNaN(from)) move(from, index);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setOverIndex(null);
  };

  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0 || !partId) return;
    const formData = new FormData();
    Array.from(files).forEach((file) => formData.append(partId, file));
    uploadPicture.mutate({ data: formData });
    if (fileInput.current) fileInput.current.value = "";
  };

  const handleRemove = () => {
    if (!deleteId) return;
    removeImage.mutate(deleteId, {
      ...deleteHandlers,
      onSettled: () => setDeleteId(null),
    });
  };

  const count = photos.length;
  const bgStaged = !!optimisation?.removeBackgroundFirstImage;
  const rulesStaged = !!optimisation?.applyImageRules;
  const reorderBusy = patchPicture.isLoading;

  return (
    <Modal open={open} setOpen={() => onClose()} width="max-w-5xl">
      <ConfirmModal
        open={deleteId != null}
        setOpen={(value: boolean) => !value && setDeleteId(null)}
        title="Delete this photo?"
        message="It is removed from the part straight away. The live eBay listing keeps it until the listing is relisted."
        confirmButtonText="Delete"
        onConfirm={handleRemove}
        isLoading={removeImage.isLoading}
      />

      {part && (
        <div className="flex max-h-[85vh] flex-col">
          <header className="flex items-start justify-between gap-4 border-b border-gray-200 pb-3 pr-8">
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Photos</p>
              <h2 className="truncate text-lg font-semibold text-gray-900">{part.category?.name || part.title || "Part"}</h2>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-600">
                {part.vehicle?.vrm && (
                  <span className="rounded bg-slate-900 px-1.5 py-0.5 font-mono font-semibold tracking-wider text-amber-300">{part.vehicle.vrm}</span>
                )}
                {part.tagNumber && <span>{part.tagNumber}</span>}
                <span className="text-gray-400">·</span>
                <span>
                  <strong className={count > MIN_IMAGE_COUNT ? "text-green-700" : "text-amber-800"}>
                    {count} photo{count === 1 ? "" : "s"}
                  </strong>
                  {count <= MIN_IMAGE_COUNT ? ` — eBay rewards ${MIN_IMAGE_COUNT} or more` : ""}
                </span>
              </div>
            </div>
          </header>

          <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 py-3">
            <input ref={fileInput} type="file" accept=".jpg,.jpeg,.png" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} />
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              disabled={uploadPicture.isLoading}
              className="inline-flex h-9 items-center gap-1.5 rounded-md bg-primary-600 px-3 text-[13px] font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
            >
              <PlusIcon className="h-4 w-4" aria-hidden="true" />
              {uploadPicture.isLoading ? "Uploading…" : "Add photos"}
            </button>
            <button
              type="button"
              onClick={() => onStageFlag(part, "removeBackgroundFirstImage", !bgStaged)}
              disabled={staging}
              aria-pressed={bgStaged}
              className={classNames(
                "inline-flex h-9 items-center rounded-md border px-3 text-[13px] font-semibold transition disabled:opacity-50",
                bgStaged ? "border-amber-400 bg-amber-100 text-amber-900" : "border-amber-300 bg-white text-amber-800 hover:bg-amber-50"
              )}
            >
              {bgStaged ? "Background removal staged" : "Remove background on 1st"}
            </button>
            <button
              type="button"
              onClick={() => onStageFlag(part, "applyImageRules", !rulesStaged)}
              disabled={staging}
              aria-pressed={rulesStaged}
              className={classNames(
                "inline-flex h-9 items-center rounded-md border px-3 text-[13px] font-semibold transition disabled:opacity-50",
                rulesStaged ? "border-sky-400 bg-sky-100 text-sky-900" : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
              )}
            >
              {rulesStaged ? "Image rules staged" : "Apply image rules"}
            </button>
            <span className="ml-auto text-xs text-gray-500">Drag a photo to reorder, or use the arrows. Photo 1 is the eBay gallery image.</span>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto py-4">
            {isLoading && !partDetails ? (
              <div className="flex justify-center py-16">
                <LoadingWheel />
              </div>
            ) : (
              <ul className={classNames("grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4", reorderBusy && "opacity-70")}>
                {photos.map((image, index) => {
                  const dragging = dragIndex === index;
                  const dropTarget = overIndex === index && dragIndex !== null && dragIndex !== index;
                  return (
                    <li
                      key={image.id}
                      draggable={!reorderBusy}
                      onDragStart={handleDragStart(index)}
                      onDragOver={handleDragOver(index)}
                      onDrop={handleDrop(index)}
                      onDragEnd={handleDragEnd}
                      className={classNames(
                        "relative flex flex-col gap-2 rounded-lg border bg-white p-2 transition",
                        dragging ? "border-primary-400 opacity-50" : dropTarget ? "border-primary-500 ring-2 ring-primary-200" : "border-gray-200"
                      )}
                    >
                      <div className="relative aspect-square overflow-hidden rounded-md bg-slate-100">
                        {image.thumbnailUrl || image.url ? (
                          <img src={image.thumbnailUrl || image.url} alt={`${index + 1} of ${photos.length}`} className="h-full w-full object-contain" draggable={false} />
                        ) : (
                          <PhotoIcon className="m-auto h-10 w-10 text-slate-400" aria-hidden="true" />
                        )}
                        <span className="absolute left-2 top-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-gray-900 text-xs font-bold text-white">
                          {index + 1}
                        </span>
                        {index === 0 && (
                          <span className="absolute right-2 top-2 rounded-full bg-primary-600 px-2 py-0.5 text-[10px] font-semibold text-white">Gallery</span>
                        )}
                        {index === 0 && bgStaged && (
                          <span className="absolute bottom-2 left-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800">BG removal staged</span>
                        )}
                        {dropTarget && (
                          <span className="absolute inset-x-0 bottom-0 bg-primary-600/90 py-1 text-center text-[11px] font-semibold text-white">
                            Drop to make this photo {index + 1}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => move(index, index - 1)}
                          disabled={index === 0 || reorderBusy}
                          aria-label="Move left"
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 text-slate-600 hover:bg-gray-50 disabled:text-gray-300"
                        >
                          <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          onClick={() => move(index, index + 1)}
                          disabled={index === photos.length - 1 || reorderBusy}
                          aria-label="Move right"
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 text-slate-600 hover:bg-gray-50 disabled:text-gray-300"
                        >
                          <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteId(image.id)}
                          disabled={reorderBusy}
                          aria-label="Delete photo"
                          className="ml-auto inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 text-red-600 hover:bg-red-50"
                        >
                          <TrashIcon className="h-4 w-4" aria-hidden="true" />
                        </button>
                      </div>
                    </li>
                  );
                })}

                <li>
                  <button
                    type="button"
                    onClick={() => fileInput.current?.click()}
                    disabled={uploadPicture.isLoading}
                    className="flex aspect-square w-full flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-gray-300 text-gray-500 transition hover:border-primary-400 hover:text-primary-700 disabled:opacity-50"
                  >
                    <PlusIcon className="h-6 w-6" aria-hidden="true" />
                    <span className="text-sm font-medium">Add photo {count + 1}</span>
                    <span className="text-[11px]">or choose several</span>
                  </button>
                </li>
              </ul>
            )}

            {fullPart && !isLoading && (
              <div className="mt-4 rounded-lg border border-gray-200">
                <button
                  type="button"
                  onClick={() => setEditorOpen((value) => !value)}
                  aria-expanded={editorOpen}
                  className="flex w-full items-center justify-between px-3 py-2 text-left text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Crop, edit, remove backgrounds now, auto order or apply image settings
                  <ChevronDownIcon className={classNames("h-4 w-4 transition-transform", editorOpen && "rotate-180")} aria-hidden="true" />
                </button>
                {editorOpen && (
                  <div className="border-t border-gray-200 p-2">
                    <EditListingImages part={fullPart} onlyPartImages />
                  </div>
                )}
              </div>
            )}
          </div>

          <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-3">
            <p className="text-xs text-gray-500">
              Photo changes save to the part straight away — they are not staged. Background removal and image rules run when the listing is relisted.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-9 items-center rounded-md bg-primary-600 px-4 text-sm font-semibold text-white hover:bg-primary-700"
            >
              Done
            </button>
          </footer>
        </div>
      )}
    </Modal>
  );
}
